// src/components/Process/Process.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake,
  faCompassDrafting,
  faLayerGroup,
  faWandMagicSparkles,
  faChartLine,
  faArrowsRotate,
} from "@fortawesome/free-solid-svg-icons";
import "./Process.css";

const Process = () => {
  const steps = [
    {
      title: "Discovery & goals",
      description:
        "We start with a short call to understand your business, audience, and what success means (leads, sales, bookings, or visibility).",
      icon: faHandshake,
      label: "Step 01",
      signal: "Clear goals + scope before we build",
    },
    {
      title: "Audit & quick wins",
      description:
        "We review your website, speed, SEO basics, and conversion flow to spot high-impact fixes you can apply immediately.",
      icon: faCompassDrafting,
      label: "Step 02",
      signal: "Prioritized fixes list (not noise)",
    },
    {
      title: "Plan the system",
      description:
        "We map pages, content, tracking, and lead flow—so design, development, and marketing work together like one system.",
      icon: faLayerGroup,
      label: "Step 03",
      signal: "Structure first → fewer reworks",
    },
    {
      title: "Design & build",
      description:
        "We design clean UI and build fast, responsive pages with proper components, SEO structure, and performance in mind.",
      icon: faWandMagicSparkles,
      label: "Step 04",
      signal: "Modern UI + clean code + speed",
    },
    {
      title: "Launch & optimize",
      description:
        "We ship, verify tracking, submit SEO essentials, and start refining based on real user behavior and lead quality.",
      icon: faChartLine,
      label: "Step 05",
      signal: "Data-backed iteration after launch",
    },
    {
      title: "Support & scale",
      description:
        "Ongoing improvements: new pages, SEO content, ads/landing pages, automation, and monthly performance check-ins.",
      icon: faArrowsRotate,
      label: "Step 06",
      signal: "Long-term growth without chaos",
    },
  ];

  return (
    <section className="proc3" aria-label="Our delivery process">
      <div className="proc3-wrap">
        {/* Header */}
        <header className="proc3-head">
          <p className="proc3-kicker">Our process</p>
          <h2>From idea to growth — delivered in a clean, repeatable system.</h2>
          <p className="proc3-sub">
            We don't just "make a website." We build a growth-ready foundation:
            structure, performance, tracking, SEO, and conversion flow — all aligned.
          </p>
        </header>

        <div className="proc3-layout">
          {/* Steps */}
          <div className="proc3-flow">
            <div className="proc3-rail" aria-hidden="true" />

            <ol className="proc3-grid">
              {steps.map((s, i) => (
                <li className="proc3-card" key={s.title}>
                  <div className="proc3-top">
                    <div className="proc3-step">
                      <span className="proc3-stepNo">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="proc3-stepLbl">{s.label}</span>
                    </div>

                    <div className="proc3-ico" aria-hidden="true">
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                  </div>

                  <h3 className="proc3-title">{s.title}</h3>
                  <p className="proc3-desc">{s.description}</p>

                  <div className="proc3-signal">
                    <span className="proc3-dot" aria-hidden="true" />
                    <span>{s.signal}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Aside */}
          <aside className="proc3-aside" aria-label="What you get">
            <div className="proc3-asideCard">
              <p className="proc3-asideKicker">What you get</p>
              <h3>Clarity, speed, and measurable progress.</h3>
              <p>
                We keep things simple: you'll always know what's happening, what's
                next, and what impact it's creating.
              </p>

              <ul className="proc3-list">
                <li>Scope + roadmap before execution</li>
                <li>Clean design and fast delivery cycles</li>
                <li>Tracking + SEO basics built-in (not afterthought)</li>
                <li>Post-launch iterations based on real results</li>
              </ul>

              <div className="proc3-pills" aria-hidden="true">
                <span className="pill">Strategy</span>
                <span className="pill">Design</span>
                <span className="pill">Development</span>
                <span className="pill">SEO</span>
              </div>
            </div>
          </aside>
        </div>

        {/* KPIs */}
        <div className="proc3-stats" role="list">
          <div className="stat" role="listitem">
            <div className="k">7–21 days</div>
            <div className="d">to first live improvements (typical)</div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">Clean handoff</div>
            <div className="d">docs, access, and reusable components</div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">Ongoing support</div>
            <div className="d">updates, iterations, and scaling help</div>
          </div>
        </div>

        {/* CTA */}
        <div className="proc3-cta">
          <a className="btn btn-primary" href="/contact">
            Book a strategy call
          </a>
          <a className="btn btn-ghost" href="/services">
            Explore services
          </a>
        </div>
      </div>
    </section>
  );
};

export default Process;
